import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { BarChart3, BookOpen, Brain, Cog, Heart, Mail, MapPin, Phone, Target, Users } from "lucide-react";
import { Btn, Eyebrow } from "@/components/site/bits";
import { Reveal } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { NcsDiagram } from "@/components/site/NcsDiagram";
import { BOOK, BOOK_URL, CONTACT, IMAGES, STEPPERS } from "@/data/site";

const soon = () =>
    toast("Consultation scheduling is by phone or email for now — details are at the bottom of the page.");

const heroLines = [
    { text: "Break the Cycle.", gold: false },
    { text: "Understand Addiction.", gold: false },
    { text: "Build a Path Forward.", gold: true },
];

const meetItems = [
    { icon: Users, title: "Person-to-Person Counseling" },
    { icon: BookOpen, title: "Education & Recovery Tools" },
    { icon: Heart, title: "Faith, Hope & Lasting Change" },
];

const knowledge = [
    {
        icon: Brain,
        title: "The Anatomy of Addiction",
        text: "Understand how addiction develops and the factors that can contribute to the cycle.",
    },
    {
        icon: Target,
        title: "The Anatomy of Relapse",
        text: "Recognize triggers, warning signs, and behaviors that may lead to relapse.",
    },
    {
        icon: Cog,
        title: "The Subconscious Mind",
        text: "Explore how thoughts, habits, past experiences, and impulsive responses influence behavior.",
    },
    {
        icon: BarChart3,
        title: "Recovery & Support Tools",
        text: "Learn practical tools and strategies for long-term change and personal growth.",
    },
];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-[#0B1F3A]">
            <motion.img
                src={IMAGES.hero}
                alt="Golden Gate Bridge at sunset, San Francisco"
                style={{ y: bgY }}
                className="absolute inset-0 h-[115%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#08162A]/95 via-[#0B1F3A]/70 to-[#0B1F3A]/25" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#08162A]/80 to-transparent" />

            <div className="relative mx-auto flex min-h-[540px] max-w-7xl flex-col justify-center px-6 py-20 lg:min-h-[620px]">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.05 }}
                >
                    <Eyebrow light>Recovery • Knowledge • Real Change</Eyebrow>
                </motion.div>

                <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
                    {heroLines.map((l, i) => (
                        <span key={l.text} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${l.gold ? "text-[#D4AF37]" : ""}`}
                                initial={{ y: "112%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {l.text}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.65 }}
                    className="mt-4 max-w-xl text-base sm:text-lg text-white/85"
                >
                    Education. Guidance. Support. A better tomorrow is possible.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.8 }}
                    className="mt-8 flex flex-wrap items-center gap-4"
                >
                    <Btn testid="hero-speak-btn" onClick={soon}>Speak with Dr. Jaquez</Btn>
                    <Btn variant="outlineLight" testid="hero-schedule-btn" arrow={false} onClick={soon}>
                        Schedule a Consultation
                    </Btn>
                </motion.div>
            </div>

            <motion.p
                initial={{ opacity: 0, rotate: -4 }}
                animate={{ opacity: 1, rotate: -4 }}
                transition={{ duration: 1, delay: 1 }}
                className="font-script pointer-events-none absolute bottom-24 right-10 hidden text-5xl text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)] lg:block xl:right-24 xl:text-6xl"
            >
                Change
                <br />
                Is Possible
                <span className="mx-auto mt-1 block h-1 w-3/4 rounded-full bg-[#D4AF37]" />
            </motion.p>
        </section>
    );
};

const Meet = () => (
    <section data-testid="meet-section" className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 sm:py-24 lg:grid-cols-[1.4fr_1fr]">
            <Reveal>
                <Eyebrow>Meet Dr. Sergio Jaquez</Eyebrow>
                <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0B1F3A]">
                    Compassion. Understanding.
                    <br />A Path Forward.
                </h2>
                <p className="mt-5 max-w-xl text-base text-[#4A5568] leading-relaxed">
                    Dr. Sergio Jaquez provides education, personal guidance, and recovery-focused support designed to
                    help individuals better understand addiction, recognize behavioral patterns, and develop tools for
                    positive change.
                </p>
                <div className="mt-10 grid gap-6 sm:grid-cols-3">
                    {meetItems.map(({ icon: Icon, title }) => (
                        <div key={title} className="flex items-start gap-3">
                            <Icon className="mt-0.5 h-7 w-7 shrink-0 text-[#0B1F3A]" strokeWidth={1.6} />
                            <p className="text-sm font-bold leading-snug text-[#0B1F3A]">{title}</p>
                        </div>
                    ))}
                </div>
            </Reveal>
            <Reveal delay={0.15} className="flex items-center border-l-2 border-[#C9A227]/60 pl-8">
                <blockquote>
                    <p className="text-xl sm:text-2xl font-medium italic leading-relaxed text-[#0B1F3A]">
                        “You don’t have to navigate this alone. There is hope, and there is a way forward.”
                    </p>
                    <footer className="mt-4 text-sm font-semibold text-[#4A5568]">— Dr. Sergio Jaquez</footer>
                </blockquote>
            </Reveal>
        </div>
    </section>
);

const FrameworkBand = () => (
    <section data-testid="framework-section" className="relative overflow-hidden bg-[#08162A]">
        <img src={IMAGES.framework} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08162A]/95 via-[#08162A]/80 to-[#08162A]/55" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 sm:py-24 lg:grid-cols-2">
            <Reveal>
                <Eyebrow light>The NCS Framework</Eyebrow>
                <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
                    A More Complete Approach
                    <br />
                    to Understanding Recovery
                </h2>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80">
                    The NCS Framework is Dr. Sergio Jaquez’s approach to exploring the different factors that can
                    influence addiction, behavior, relapse, and recovery. Rather than focusing only on the addiction
                    itself, this framework looks at the whole person — including thought patterns, behaviors, triggers,
                    the subconscious mind, and other Neuro-Bio-Spiritual Dynamics that may affect long-term change.
                </p>
                <div className="mt-8">
                    <Btn testid="framework-learn-more-btn" to="/framework">Learn More About the Framework</Btn>
                </div>
            </Reveal>
            <Reveal delay={0.15}>
                <NcsDiagram />
                <p className="font-script mt-6 text-center text-3xl text-white/95 sm:text-4xl">
                    “Understand the Cycle. Change the Outcome.”
                </p>
            </Reveal>
        </div>
    </section>
);

const Knowledge = () => (
    <section data-testid="knowledge-section" className="bg-[#F9F8F3]">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
            <Reveal>
                <Eyebrow>What You’ll Learn</Eyebrow>
                <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0B1F3A]">
                    Knowledge Creates Change
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#4A5568]">
                    Education is a powerful tool. Understanding the patterns behind addiction can help you make
                    different choices and build a healthier future.
                </p>
            </Reveal>
            <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
                {knowledge.map(({ icon: Icon, title, text }, i) => (
                    <Reveal key={title} delay={i * 0.08}>
                        <div data-testid={`knowledge-card-${i}`} className="group">
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0B1F3A] text-[#D4AF37] transition-transform duration-300 group-hover:scale-110">
                                <Icon className="h-6 w-6" strokeWidth={1.8} />
                            </div>
                            <h3 className="mt-4 text-base font-bold leading-snug text-[#0B1F3A]">{title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-[#4A5568]">{text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const PublishedBand = () => (
    <section data-testid="published-band" className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 sm:py-24 lg:grid-cols-[auto_1.2fr_1fr]">
            <Reveal className="flex justify-center">
                <img
                    src={BOOK.cover}
                    alt="Breaking the Chains — A Neuro-Bio-Spiritual Path to Freedom, book cover"
                    data-testid="home-book-cover"
                    className="w-48 rounded-md shadow-[0_30px_60px_-20px_rgba(8,22,42,0.55)] transition-transform duration-500 hover:-translate-y-2 sm:w-56"
                />
            </Reveal>

            <Reveal delay={0.1}>
                <Eyebrow>Published Work</Eyebrow>
                <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B1F3A]">
                    {BOOK.title}
                </h2>
                <p className="mt-1 text-base font-semibold text-[#C9A227]">{BOOK.subtitle}</p>
                <p className="mt-4 max-w-lg text-sm sm:text-base leading-relaxed text-[#4A5568]">
                    A Neuro-Clinical-Spiritual Framework for Breaking Addictive Cycles and Restoring the Power of
                    Choice.
                </p>
                <div className="mt-7 flex flex-wrap gap-4">
                    <Btn variant="navy" testid="home-learn-book-btn" to="/published-work/breaking-the-chains">
                        Learn More About the Book
                    </Btn>
                    <Btn variant="outlineNavy" testid="home-purchase-book-btn" href={BOOK_URL} cart>
                        Purchase the Book
                    </Btn>
                </div>
            </Reveal>

            <Reveal delay={0.2}>
                <div className="rounded-xl border border-[#C9A227]/30 bg-[#F9F8F3] p-7">
                    <Eyebrow>11th Steppers</Eyebrow>
                    <h3 className="mt-3 text-xl font-extrabold tracking-tight text-[#0B1F3A]">
                        11th Steppers Fellowship
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#4A5568]">
                        A Christ-centered community supporting individuals in sustained recovery through education,
                        fellowship, and spiritual growth.
                    </p>
                    <img src={STEPPERS.logo} alt="11th Steppers Fellowship logo" className="mx-auto mt-5 w-32" />
                    <div className="mt-5 space-y-3 text-sm text-[#1A202C]">
                        <p className="flex items-start gap-3">
                            <Users className="mt-0.5 h-5 w-5 shrink-0 text-[#0B1F3A]" />
                            <span className="font-semibold">{STEPPERS.meeting}</span>
                        </p>
                        <p className="flex items-start gap-3">
                            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#0B1F3A]" />
                            <span>
                                <span className="font-semibold">{STEPPERS.place}</span>
                                <br />
                                {STEPPERS.address}
                            </span>
                        </p>
                    </div>
                    <div className="mt-6">
                        <Btn variant="navy" testid="steppers-learn-more-btn" to="/11th-steppers" className="w-full">
                            Learn More About 11th Steppers
                        </Btn>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

const CtaBand = () => (
    <section data-testid="cta-band" className="relative overflow-hidden bg-[#0B1F3A]">
        <img src={IMAGES.cta} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08162A]/90 via-[#0B1F3A]/70 to-[#08162A]/60" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 py-20 lg:grid-cols-[1.3fr_1fr]">
            <Reveal>
                <p className="font-script text-4xl text-white sm:text-5xl">You Don’t Have to Do This Alone.</p>
                <p className="mt-3 text-base text-white/85">Take the first step. Reach out today.</p>
                <div className="mt-7 flex flex-wrap gap-4">
                    <Btn testid="cta-speak-btn" onClick={soon}>Speak with Dr. Jaquez</Btn>
                    <Btn variant="outlineLight" testid="cta-schedule-btn" arrow={false} onClick={soon}>
                        Schedule a Consultation
                    </Btn>
                </div>
            </Reveal>
            <Reveal delay={0.15}>
                <div className="space-y-4 text-white">
                    <p data-testid="cta-phone" className="flex items-center gap-3 text-base font-semibold">
                        <Phone className="h-5 w-5 text-[#D4AF37]" /> {CONTACT.phone}
                    </p>
                    <p data-testid="cta-email" className="flex items-center gap-3 text-base font-semibold">
                        <Mail className="h-5 w-5 text-[#D4AF37]" /> {CONTACT.email}
                    </p>
                    <p data-testid="cta-location" className="flex items-center gap-3 text-base font-semibold">
                        <MapPin className="h-5 w-5 text-[#D4AF37]" /> {CONTACT.location}
                    </p>
                </div>
            </Reveal>
        </div>
    </section>
);

export default function Home() {
    return (
        <main>
            <Hero />
            <Marquee />
            <Meet />
            <FrameworkBand />
            <Knowledge />
            <PublishedBand />
            <CtaBand />
        </main>
    );
}
